/**
 * Custom popup component for displaying messages to users
 */

/**
 * Display a custom styled popup message with optional auto-close
 * @param message - The message to display (can include HTML)
 * @param autoClose - Auto-close delay in milliseconds (0 = no auto-close)
 * @param buttonText - Text for the OK button
 * @param onClose - Optional callback fired once when the popup is dismissed
 *                  (OK button, close button, overlay click, or auto-close)
 */
export function showCustomPopup(
  message: string,
  autoClose: number = 0,
  buttonText: string = "OK",
  onClose?: () => void,
): void {
  // Clear only a previous *message* popup. A blanket '.custom-popup-overlay'
  // sweep also destroyed the cart page's own confirm dialog (leaving its
  // promise unsettled, so clearCart's .then never ran) and showCustomConfirm's
  // overlay. Each dialog system now clears only its own instances.
  $(".custom-message-overlay").remove();

  const popupHTML = `
    <div class="custom-popup-overlay custom-message-overlay">
      <div class="custom-popup">
        <button class="custom-popup__close" aria-label="Close">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        <div class="custom-popup__message">${message}</div>
        <button class="custom-popup__ok">${buttonText}</button>
      </div>
    </div>
  `;

  $("body").append(popupHTML);

  const $overlay = $(".custom-message-overlay");

  setTimeout(() => {
    $overlay.addClass("show");
  }, 10);

  let closed = false;
  function closePopup(): void {
    if (closed) return;
    closed = true;
    $overlay.removeClass("show");
    setTimeout(() => {
      $overlay.remove();
    }, 200);
    onClose?.();
  }

  $overlay.find(".custom-popup__ok").on("click", closePopup);
  $overlay.find(".custom-popup__close").on("click", closePopup);

  $overlay.on("click", function (e: JQuery.ClickEvent) {
    if ($(e.target).hasClass("custom-popup-overlay")) {
      closePopup();
    }
  });

  if (autoClose > 0) {
    const fadeStartTime = Math.max(0, autoClose - 500);

    setTimeout(() => {
      $overlay.css({
        transition: "opacity 500ms ease-out",
        opacity: "0.5",
      });
    }, fadeStartTime);

    setTimeout(closePopup, autoClose);
  }
}

window.showCustomPopup = showCustomPopup;

/**
 * Display a two-button confirmation dialog.
 *
 * Left button is the safe default (focused on open, ESC/Enter/overlay all
 * trigger it). Right button is for the destructive / proceed-anyway action.
 */
export function showCustomConfirm(opts: {
  message: string;
  leftButtonText: string;
  rightButtonText: string;
  onConfirm: () => void;
  onCancel?: () => void;
}): void {
  $(".custom-confirm-overlay").remove();

  const html = `
    <div class="custom-popup-overlay custom-confirm-overlay">
      <div class="custom-popup custom-confirm">
        <div class="custom-popup__message">${opts.message}</div>
        <div class="custom-confirm__actions">
          <button type="button" class="custom-confirm__cancel">${opts.leftButtonText}</button>
          <button type="button" class="custom-confirm__ok">${opts.rightButtonText}</button>
        </div>
      </div>
    </div>
  `;

  $("body").append(html);

  const $overlay = $(".custom-confirm-overlay");
  setTimeout(() => $overlay.addClass("show"), 10);

  let closed = false;
  function close(fn: "confirm" | "cancel"): void {
    if (closed) return;
    closed = true;
    $(document).off("keydown.customConfirm");
    $overlay.removeClass("show");
    setTimeout(() => $overlay.remove(), 200);
    if (fn === "confirm") {
      opts.onConfirm();
    } else {
      opts.onCancel?.();
    }
  }

  $overlay.find(".custom-confirm__cancel").on("click", () => close("cancel"));
  $overlay.find(".custom-confirm__ok").on("click", () => close("confirm"));
  $overlay.on("click", function (e: JQuery.ClickEvent) {
    if ($(e.target).hasClass("custom-confirm-overlay")) {
      close("cancel");
    }
  });

  $(document).on("keydown.customConfirm", function (e: JQuery.KeyDownEvent) {
    if (e.key === "Escape" || e.key === "Enter") {
      e.preventDefault();
      close("cancel");
    }
  });

  // Focus the safe (left) button on open
  setTimeout(() => {
    ($overlay.find(".custom-confirm__cancel")[0] as HTMLElement)?.focus();
  }, 0);
}
